import { Link, Navigate, useLocation } from "react-router-dom";
import { useSubscription } from "@/context/SubscriptionContext";

/**
 * Whole-app gate. Renders children when subscription grants access; otherwise
 * shows a clean block screen or redirects to plan selection.
 *
 * - Whitelist of subscription-recovery routes is always allowed.
 */
const ALWAYS_ALLOWED = new Set([
  "/subscription/select-plan",
  "/subscription/change-plan",
  "/subscription/manage",
  "/subscription/billing-status",
  "/subscription/success",
  "/subscription/cancelled",
  "/pricing",
]);

export function SubscriptionGate({ children }) {
  const sub = useSubscription();
  const loc = useLocation();
  // M5 (2026-07-10): show a minimal loader while entitlements resolve
  // instead of rendering children optimistically. Prevents a flash of
  // protected UI before the backend guards close the door.
  if (!sub || sub.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="subscription-loading">
        <div className="h-8 w-8 rounded-full border-2 border-slate-300 border-t-cyan-600 animate-spin" />
      </div>
    );
  }
  const path = loc.pathname || "/";
  const isWhitelisted = ALWAYS_ALLOWED.has(path);
  if (isWhitelisted) return children;

  if (sub.needsPlanSelection) {
    if (sub.canBilling) {
      return <Navigate to="/subscription/change-plan" replace />;
    }
    return <ContactAdminScreen reason="Your organization subscription needs attention." />;
  }
  if (sub.isBlocked) {
    if (sub.canBilling) return <Navigate to="/subscription/billing-status" replace />;
    return <ContactAdminScreen reason="Your workspace is currently restricted." />;
  }
  return children;
}

function ContactAdminScreen({ reason }) {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#FFFFF8" }} data-testid="subscription-contact-admin">
      <div className="co-card max-w-md w-full p-8 text-center">
        <div className="text-3xl">⛔</div>
        <div className="text-xl font-bold text-slate-900 mt-2">{reason}</div>
        <div className="text-sm text-slate-600 mt-1">Please contact your administrator to restore access.</div>
        <Link to="/pricing" className="inline-block mt-4 text-cyan-700 hover:underline text-xs">View available plans →</Link>
      </div>
    </div>
  );
}

/** Feature-key wrapper for in-app sections. Default-deny (M5). */
export function FeatureGate({ featureKey, children, fallback = null }) {
  const sub = useSubscription();
  if (!sub || sub.loading) return null;
  if (sub.hasFeature(featureKey)) return children;
  return fallback || (
    <div className="co-card p-6 text-center" data-testid={`feature-locked-${featureKey}`}>
      <div className="text-sm text-slate-600">This feature is not included in your current plan.</div>
      {sub.canBilling ? (
        <Link to="/subscription/change-plan" className="inline-block mt-2 text-cyan-700 hover:underline text-sm">View plans →</Link>
      ) : (
        <div className="mt-1 text-xs text-slate-500">Ask your workspace Owner/Admin to upgrade.</div>
      )}
    </div>
  );
}
